import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

interface ReceiverDetails {
    name: string;
    iban: string;
    edrpou: string;
    bankName: string;
    mfo: string;
}

interface GeneratedInvoice {
    number: string;
    timestamp: string;
    payerDetails: string;
}

interface Props {
    generatedInvoice: GeneratedInvoice;
    selectedPlanName: string;
    amount: string;
    receiverDetails: ReceiverDetails;
    onDownloadPdf: () => void;
}

const InvoicePreviewCard = ({
    generatedInvoice,
    selectedPlanName,
    amount,
    receiverDetails,
    onDownloadPdf,
}: Props) => {
    const { t } = useTranslation('Tariff');
    const payerLines = generatedInvoice.payerDetails.split('\n');

    return (
        <div className="bg-body p-3 rounded">
            <div className="border-bottom pb-2 mb-3">
                <h6 className="mb-1">{t('invoicePreviewTitle')}</h6>
                <p className="text-muted f-12 mb-0">
                    {`${t('invoiceNumberLabel')}: ${generatedInvoice.number}`}
                </p>
            </div>

            <div className="border-bottom pb-2 mb-3">
                <div className="d-flex justify-content-between mb-1">
                    <span className="text-muted">{t('invoicePlanLabel')}</span>
                    <span className="fw-medium">{selectedPlanName}</span>
                </div>
                <div className="d-flex justify-content-between">
                    <span className="text-muted">{t('invoiceAmountLabel')}</span>
                    <span className="fw-medium">{amount}</span>
                </div>
            </div>

            <div className="border-bottom pb-2 mb-3">
                <p className="h6 text-muted mb-2">{t('receiverDetailsTitle')}</p>
                <div className="d-flex justify-content-between mb-1">
                    <span className="text-muted">{t('receiverNameLabel')}</span>
                    <span className="fw-medium text-end">{receiverDetails.name}</span>
                </div>
                <div className="d-flex justify-content-between mb-1">
                    <span className="text-muted">{t('receiverIbanLabel')}</span>
                    <span className="fw-medium text-end">{receiverDetails.iban}</span>
                </div>
                <div className="d-flex justify-content-between mb-1">
                    <span className="text-muted">{t('receiverEdrpouLabel')}</span>
                    <span className="fw-medium text-end">{receiverDetails.edrpou}</span>
                </div>
                <div className="d-flex justify-content-between mb-1">
                    <span className="text-muted">{t('receiverBankLabel')}</span>
                    <span className="fw-medium text-end">{receiverDetails.bankName}</span>
                </div>
                <div className="d-flex justify-content-between">
                    <span className="text-muted">{t('receiverMfoLabel')}</span>
                    <span className="fw-medium text-end">{receiverDetails.mfo}</span>
                </div>
            </div>

            <div className="mb-3">
                <p className="h6 text-muted mb-2">{t('payerDetailsTitle')}</p>
                {payerLines.map((line, index) => (
                    <p key={index} className={index < payerLines.length - 1 ? 'mb-1' : 'mb-0'}>
                        {line || '\u00A0'}
                    </p>
                ))}
            </div>

            <Button type="button" variant="success" className="w-100" onClick={onDownloadPdf}>
                {t('downloadInvoicePdf')}
            </Button>
        </div>
    );
};

export default InvoicePreviewCard;
